// src/components/ProfilePage.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaUser, FaEdit, FaCamera, FaTrash, FaCalendarAlt, FaIdCard, FaPhone, FaEnvelope, FaMapMarkerAlt, FaBriefcase, FaGraduationCap, FaUserCheck, FaBirthdayCake, FaCheckCircle, FaClock, FaExclamationTriangle, FaBan, FaBuilding, FaGlobe, FaStore, FaStar } from 'react-icons/fa';
import PublicLayout from './public/PublicLayout';

const ProfilePage = ({ user, onLogout }) => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem('authToken') ||
        localStorage.getItem('userToken') ||
        localStorage.getItem('directorToken') ||
        localStorage.getItem('adminToken');

      console.log('🔍 [ProfilePage] Fetching profile...');
      console.log('🔑 Token found:', token ? 'Yes' : 'No');

      if (!token) {
        console.warn('⚠️ No authentication token found!');
        setLoading(false);
        return;
      }

      const API_BASE = process.env.REACT_APP_API_URL;
      if (!API_BASE) {
        throw new Error('API URL not configured');
      }
      const response = await fetch(`${API_BASE}/users/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('📊 Profile API Response Status:', response.status);

      if (response.ok) {
        const result = await response.json();
        console.log('✅ Profile Data:', result.data.profile);
        setProfile(result.data.profile);
        setFormData(result.data.profile);
      } else {
        const errorData = await response.json();
        console.error('❌ Profile fetch error:', errorData);
        toast.error(errorData.message || 'Failed to load profile');
      }
    } catch (error) {
      console.error('❌ Error fetching profile:', error);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveProfile = async () => {
    try {
      const token = localStorage.getItem('authToken') ||
        localStorage.getItem('userToken') ||
        localStorage.getItem('directorToken') ||
        localStorage.getItem('adminToken');

      if (!token) {
        toast.error('Please log in to save your profile');
        return;
      }

      // Only send editable fields that the API accepts
      const editableFields = {
        title: formData.title,
        first_name: formData.first_name,
        last_name: formData.last_name,
        phone: formData.phone,
        date_of_birth: formData.date_of_birth,
        gender: formData.gender,
        street_address: formData.street_address,
        city: formData.city,
        state_province: formData.state_province,
        postal_code: formData.postal_code,
        country: formData.country,
        institution: formData.institution,
        job_title: formData.job_title || formData.profession, // Support both field names
        specialization: formData.specialization,
        experience_years: formData.experience_years || formData.years_of_experience, // Support both field names
        bio: formData.bio,
        address: formData.address
      };

      // Remove undefined/null values
      Object.keys(editableFields).forEach(key => {
        if (editableFields[key] === undefined) {
          delete editableFields[key];
        }
      });

      console.log('📤 Sending profile update:', editableFields);

      const API_BASE = process.env.REACT_APP_API_URL;
      if (!API_BASE) {
        throw new Error('API URL not configured');
      }
      const response = await fetch(`${API_BASE}/users/profile`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editableFields)
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('❌ Profile update failed:', errorData);
        throw new Error(errorData.message || 'Failed to update profile');
      }

      const result = await response.json();
      console.log('✅ Profile update successful:', result);
      toast.success('Profile updated successfully!');
      setProfile(formData);
      setIsEditing(false);
      await fetchProfile(); // Refresh profile data
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error(error.message || 'Failed to update profile');
    }
  };

  const handleProfilePictureUpload = async (file) => {
    try {
      setUploading(true);
      const token = localStorage.getItem('authToken') ||
        localStorage.getItem('userToken') ||
        localStorage.getItem('directorToken') ||
        localStorage.getItem('adminToken');

      if (!token) {
        toast.error('Please log in to upload a profile picture');
        setUploading(false);
        return;
      }

      const formData = new FormData();
      formData.append('profilePicture', file);

      const API_BASE = process.env.REACT_APP_API_URL;
      if (!API_BASE) {
        throw new Error('API URL not configured');
      }
      const res = await fetch(`${API_BASE}/users/profile/picture`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || 'Failed to upload profile picture');
      }

      const data = await res.json();
      console.log('Uploaded:', data);
      toast.success('Profile picture updated successfully!');
      await fetchProfile(); // Refresh to get new picture URL
    } catch (error) {
      console.error('Error uploading profile picture:', error);
      toast.error(error.message || 'Failed to upload profile picture');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteProfilePicture = async () => {
    if (!window.confirm('Are you sure you want to delete your profile picture?')) {
      return;
    }

    try {
      const token = localStorage.getItem('authToken') ||
        localStorage.getItem('userToken') ||
        localStorage.getItem('directorToken') ||
        localStorage.getItem('adminToken');

      if (!token) {
        toast.error('Please log in to delete your profile picture');
        return;
      }

      const API_BASE = process.env.REACT_APP_API_URL;
      if (!API_BASE) {
        throw new Error('API URL not configured');
      }
      const response = await fetch(`${API_BASE}/users/profile/picture`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete profile picture');
      }

      toast.success('Profile picture deleted successfully!');
      await fetchProfile(); // Refresh profile data
    } catch (error) {
      console.error('Error deleting profile picture:', error);
      toast.error(error.message || 'Failed to delete profile picture');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not specified';
    // Use split to avoid timezone shift (prevents Dec 29 showing as Dec 28)
    const parts = dateString.split('T')[0].split('-');
    if (parts.length !== 3) return 'Not specified';
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[parseInt(parts[1]) - 1]} ${parseInt(parts[2])}, ${parts[0]}`;
  };

  const getMembershipStatusBadge = (status) => {
    const statusConfig = {
      active: {
        color: 'success',
        icon: FaCheckCircle,
        text: 'Active Member',
        description: 'Payment up to date'
      },
      pending: {
        color: 'warning',
        icon: FaClock,
        text: 'Pending Approval',
        description: 'Just applied'
      },
      expired: {
        color: 'danger',
        icon: FaExclamationTriangle,
        text: 'Inactive Member',
        description: 'Expired or not renewed'
      },
      suspended: {
        color: 'secondary',
        icon: FaBan,
        text: 'Suspended/Revoked',
        description: 'Account suspended'
      },
      expiring_soon: {
        color: 'warning',
        icon: FaExclamationTriangle,
        text: 'Expiring Soon',
        description: 'Renewal needed'
      }
    };

    const config = statusConfig[status] || statusConfig.pending;
    const StatusIcon = config.icon;

    return (
      <div>
        <span className={`badge bg-${config.color} d-inline-flex align-items-center gap-2 px-3 py-2`}>
          <StatusIcon size={14} />
          {config.text}
        </span>
        <p className="text-muted small mt-1 mb-0">{config.description}</p>
      </div>
    );
  };

  if (loading) {
    return (
      <PublicLayout>
        <div className="container py-5">
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-4 text-muted">Loading your profile...</p>
          </div>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <div className="bg-light py-4">
        <div className="container">
          {/* Back Button and Page Header */}
          <div className="mb-4">
            <Link to="/dashboard" className="btn btn-outline-secondary btn-sm mb-3 d-inline-flex align-items-center">
              <i className="fas fa-arrow-left me-2"></i>
              Back to Dashboard
            </Link>
            <h1 className="h3 fw-bold text-dark">My Profile</h1>
            <p className="text-muted">Manage your personal information and membership details</p>
          </div>

          <div className="row g-4">
            {/* Profile Picture & Basic Info */}
            <div className="col-lg-4">
              <div className="card border-0 shadow-sm">
                <div className="card-body p-4">
                  <div className="text-center">
                    <div className="position-relative d-inline-block">
                      {profile?.profile_picture_url ? (
                        <img
                          src={profile.profile_picture_url.startsWith('http')
                            ? profile.profile_picture_url
                            : `${process.env.REACT_APP_BACKEND_URL || 'https://api.africaresp.org'}${profile.profile_picture_url}`}
                          alt="Profile"
                          className="rounded-circle"
                          style={{ width: '128px', height: '128px', objectFit: 'cover', display: 'block' }}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.style.display = 'none';
                            e.target.nextElementSibling.style.display = 'flex';
                          }}
                        />
                      ) : (
                        <div
                          className="bg-secondary rounded-circle d-flex align-items-center justify-content-center mx-auto"
                          style={{ width: '128px', height: '128px' }}
                        >
                          <FaUser className="text-white" size={64} />
                        </div>
                      )}
                      {/* Hidden fallback shown only on image load error */}
                      {profile?.profile_picture_url && (
                        <div
                          className="bg-secondary rounded-circle align-items-center justify-content-center mx-auto"
                          style={{ width: '128px', height: '128px', display: 'none' }}
                        >
                          <FaUser className="text-white" size={64} />
                        </div>
                      )}
                      {uploading && (
                        <div className="position-absolute top-0 start-0 w-100 h-100 bg-dark bg-opacity-50 rounded-circle d-flex align-items-center justify-content-center">
                          <div className="spinner-border text-white" role="status">
                            <span className="visually-hidden">Uploading...</span>
                          </div>
                        </div>
                      )}
                    </div>

                    <h3 className="mt-3 h5 fw-medium text-dark">
                      {profile?.title && `${profile.title} `}{profile?.full_name || 'Complete Your Profile'}
                    </h3>
                    {profile?.membership_id && (
                      <p className="text-muted mb-1">
                        <strong className="text-primary">Member ID:</strong> {profile.membership_id}
                      </p>
                    )}
                    <p className="text-muted small">{profile?.email}</p>
                    <div className="mt-2">
                      {getMembershipStatusBadge(profile?.membership_status_display || profile?.membership_status || 'pending')}
                    </div>

                    {/* Profile Picture Actions */}
                    <div className="mt-3 d-flex justify-content-center gap-2">
                      <label className="btn btn-primary btn-sm d-inline-flex align-items-center">
                        <FaCamera className="me-2" />
                        Upload
                        <input
                          type="file"
                          className="d-none"
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) handleProfilePictureUpload(file);
                          }}
                        />
                      </label>
                      {profile?.profile_picture_url && (
                        <button
                          onClick={handleDeleteProfilePicture}
                          className="btn btn-danger btn-sm d-inline-flex align-items-center"
                        >
                          <FaTrash className="me-2" />
                          Delete
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Membership Quick Info */}
                  <div className="mt-4 pt-4 border-top">
                    <h5 className="h6 fw-bold text-dark mb-3">
                      <FaIdCard className="text-primary me-2" />
                      Membership Information
                    </h5>
                    <div className="d-grid gap-2">
                      <div className="d-flex justify-content-between align-items-center small">
                        <span className="text-muted">Type:</span>
                        <span className="fw-medium text-dark text-capitalize">{profile?.membership_type_display || profile?.membership_type || 'Not specified'}</span>
                      </div>
                      <div className="d-flex justify-content-between align-items-center small">
                        <span className="text-muted">Member Since:</span>
                        <span className="fw-medium text-dark">{formatDate(profile?.date_joined || profile?.membership_start_date)}</span>
                      </div>

                      {/* Membership Duration Display */}
                      {profile?.membership_status === 'active' && profile?.membership_start_date ? (
                        <>
                          <div className="alert alert-success mt-2 mb-0 py-2 px-3" style={{ backgroundColor: '#d4edda', borderColor: '#c3e6cb' }}>
                            <div className="small">
                              <strong className="d-block mb-1" style={{ color: '#155724' }}>
                                <FaCheckCircle className="me-1" /> Membership Duration
                              </strong>
                              <div className="d-flex justify-content-between mb-1">
                                <span className="text-muted">Start Date:</span>
                                <span className="fw-medium" style={{ color: '#155724' }}>{formatDate(profile.membership_start_date)}</span>
                              </div>
                              <div className="d-flex justify-content-between mb-1">
                                <span className="text-muted">Expiry Date:</span>
                                <span className="fw-medium" style={{ color: '#155724' }}>{formatDate(profile.membership_end_date)}</span>
                              </div>
                              <div className="d-flex justify-content-between">
                                <span className="text-muted">Status:</span>
                                <span className="fw-bold" style={{ color: '#155724' }}>Active</span>
                              </div>
                            </div>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="d-flex justify-content-between align-items-center small">
                            <span className="text-muted">Start Date:</span>
                            <span className="fw-medium text-dark">{formatDate(profile?.membership_start_date)}</span>
                          </div>
                          <div className="d-flex justify-content-between align-items-center small">
                            <span className="text-muted">Expiration Date:</span>
                            <span className="fw-medium text-dark">{formatDate(profile?.membership_expiry_date || profile?.next_renewal_date || profile?.membership_end_date)}</span>
                          </div>
                        </>
                      )}

                      {profile?.days_until_renewal && (
                        <div className="d-flex justify-content-between align-items-center small">
                          <span className="text-muted">Days Until Renewal:</span>
                          <span className={`fw-bold ${profile.days_until_renewal <= 30 ? 'text-danger' : 'text-success'}`}>
                            {profile.days_until_renewal > 0 ? profile.days_until_renewal : 'Expired'}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Profile Details */}
            <div className="col-lg-8">
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-white border-bottom d-flex justify-content-between align-items-center">
                  <h3 className="h5 mb-0">Profile Information</h3>
                  <button
                    onClick={() => {
                      if (isEditing) {
                        handleSaveProfile();
                      } else {
                        setIsEditing(true);
                      }
                    }}
                    className={`btn btn-sm d-inline-flex align-items-center ${isEditing
                      ? 'btn-success'
                      : 'btn-primary'
                      }`}
                  >
                    <FaEdit className="me-2" />
                    {isEditing ? 'Save Changes' : 'Edit Profile'}
                  </button>
                </div>

                <div className="card-body p-4">
                  <div className="row g-4">
                    {/* Personal Information */}
                    <div className="col-12">
                      <h4 className="h5 fw-bold text-dark mb-3">
                        <FaUser className="text-primary me-2" />
                        Personal Information
                      </h4>
                    </div>

                    <div className="col-md-6">
                      <label className="form-label fw-medium">Title</label>
                      {isEditing ? (
                        <select
                          value={formData.title || ''}
                          onChange={(e) => handleInputChange('title', e.target.value)}
                          className="form-select"
                        >
                          <option value="">Select</option>
                          <option>Mr.</option>
                          <option>Mrs.</option>
                          <option>Ms.</option>
                          <option>Dr.</option>
                          <option>Prof.</option>
                          <option>RRT.</option>
                          <option>RCP.</option>
                          <option>RT.</option>
                          <option>RN.</option>
                          <option>CRNA</option>
                          <option>PT/DPT</option>
                          <option>OT/OTD</option>
                          <option>SLP</option>
                          <option>PA/PA-C</option>
                          <option>NP/APRN</option>
                          <option>DDS/DMD</option>
                          <option>DVM</option>
                          <option>PharmD</option>
                          <option>OD</option>
                          <option>LCSW</option>
                          <option>LPN</option>
                          <option>LMT</option>
                          <option>RD/RDN</option>
                          <option>DO</option>
                        </select>
                      ) : (
                        <p className="text-dark mb-0">{profile?.title || 'Not specified'}</p>
                      )}
                    </div>

                    <div className="col-md-6">
                      <label className="form-label fw-medium">First Name</label>
                      {isEditing ? (
                        <input
                          type="text"
                          value={formData.first_name || ''}
                          onChange={(e) => handleInputChange('first_name', e.target.value)}
                          className="form-control"
                        />
                      ) : (
                        <p className="text-dark mb-0">{profile?.first_name || 'Not specified'}</p>
                      )}
                    </div>

                    <div className="col-md-6">
                      <label className="form-label fw-medium">Last Name</label>
                      {isEditing ? (
                        <input
                          type="text"
                          value={formData.last_name || ''}
                          onChange={(e) => handleInputChange('last_name', e.target.value)}
                          className="form-control"
                        />
                      ) : (
                        <p className="text-dark mb-0">{profile?.last_name || 'Not specified'}</p>
                      )}
                    </div>

                    <div className="col-md-6">
                      <label className="form-label fw-medium">
                        <FaEnvelope className="text-primary me-2" />
                        Email Address
                      </label>
                      <p className="text-dark mb-0">{profile?.email}</p>
                      <span className="text-muted small">Email cannot be changed</span>
                    </div>

                    <div className="col-md-6">
                      <label className="form-label fw-medium">
                        <FaPhone className="text-primary me-2" />
                        Phone Number
                      </label>
                      {isEditing ? (
                        <input
                          type="tel"
                          value={formData.phone || ''}
                          onChange={(e) => handleInputChange('phone', e.target.value)}
                          className="form-control"
                        />
                      ) : (
                        <p className="text-dark mb-0">{profile?.phone || 'Not specified'}</p>
                      )}
                    </div>

                    {profile?.membership_type !== 'vendor' && (
                      <div className="col-md-6">
                        <label className="form-label fw-medium">
                          <FaBirthdayCake className="text-primary me-2" />
                          Date of Birth
                        </label>
                        {isEditing ? (
                          <input
                            type="date"
                            value={formData.date_of_birth || ''}
                            onChange={(e) => handleInputChange('date_of_birth', e.target.value)}
                            className="form-control"
                          />
                        ) : (
                          <p className="text-dark mb-0">{formatDate(profile?.date_of_birth) || 'Not specified'}</p>
                        )}
                      </div>
                    )}

                    {profile?.membership_type !== 'vendor' && (
                      <div className="col-md-6">
                        <label className="form-label fw-medium">Gender</label>
                        {isEditing ? (
                          <select
                            value={formData.gender || ''}
                            onChange={(e) => handleInputChange('gender', e.target.value)}
                            className="form-select"
                          >
                            <option value="">Select Gender</option>
                            <option value="male">Male</option>
                            <option value="female">Female</option>
                          </select>
                        ) : (
                          <p className="text-dark mb-0 text-capitalize">{profile?.gender || 'Not specified'}</p>
                        )}
                      </div>
                    )}

                    {/* Address Information */}
                    <div className="col-12 mt-3">
                      <h4 className="h5 fw-bold text-dark mb-3">
                        <FaMapMarkerAlt className="text-primary me-2" />
                        Address Information
                      </h4>
                    </div>

                    <div className="col-12">
                      <label className="form-label fw-medium">Street Address</label>
                      {isEditing ? (
                        <textarea
                          value={formData.street_address || ''}
                          onChange={(e) => handleInputChange('street_address', e.target.value)}
                          rows={2}
                          className="form-control"
                        />
                      ) : (
                        <p className="text-dark mb-0">{profile?.street_address || profile?.vendor_street_address || 'Not specified'}</p>
                      )}
                    </div>

                    <div className="col-md-6">
                      <label className="form-label fw-medium">City</label>
                      {isEditing ? (
                        <input
                          type="text"
                          value={formData.city || ''}
                          onChange={(e) => handleInputChange('city', e.target.value)}
                          className="form-control"
                        />
                      ) : (
                        <p className="text-dark mb-0">{profile?.city || profile?.vendor_city || 'Not specified'}</p>
                      )}
                    </div>

                    <div className="col-md-6">
                      <label className="form-label fw-medium">State/Province</label>
                      {isEditing ? (
                        <input
                          type="text"
                          value={formData.state_province || ''}
                          onChange={(e) => handleInputChange('state_province', e.target.value)}
                          className="form-control"
                        />
                      ) : (
                        <p className="text-dark mb-0">{profile?.state_province || profile?.vendor_state_province || 'Not specified'}</p>
                      )}
                    </div>

                    <div className="col-md-6">
                      <label className="form-label fw-medium">Postal Code</label>
                      {isEditing ? (
                        <input
                          type="text"
                          value={formData.postal_code || ''}
                          onChange={(e) => handleInputChange('postal_code', e.target.value)}
                          className="form-control"
                        />
                      ) : (
                        <p className="text-dark mb-0">{profile?.postal_code || 'Not specified'}</p>
                      )}
                    </div>

                    <div className="col-md-6">
                      <label className="form-label fw-medium">Country</label>
                      {isEditing ? (
                        <input
                          type="text"
                          value={formData.country || ''}
                          onChange={(e) => handleInputChange('country', e.target.value)}
                          className="form-control"
                        />
                      ) : (
                        <p className="text-dark mb-0">{profile?.country || profile?.vendor_country || 'Not specified'}</p>
                      )}
                    </div>

                    {/* Professional Information */}
                    <div className="col-12 mt-3">
                      <h4 className="h5 fw-bold text-dark mb-3">
                        <FaBriefcase className="text-primary me-2" />
                        Professional Information
                      </h4>
                    </div>

                    <div className="col-md-6">
                      <label className="form-label fw-medium">Profession / Occupation</label>
                      {isEditing ? (
                        <input
                          type="text"
                          value={formData.profession || ''}
                          onChange={(e) => handleInputChange('profession', e.target.value)}
                          className="form-control"
                        />
                      ) : (
                        <p className="text-dark mb-0">{profile?.profession || 'Not specified'}</p>
                      )}
                    </div>

                    <div className="col-md-6">
                      <label className="form-label fw-medium">Years of Experience</label>
                      {isEditing ? (
                        <input
                          type="number"
                          min="0"
                          max="60"
                          value={formData.years_of_experience || ''}
                          onChange={(e) => handleInputChange('years_of_experience', e.target.value)}
                          className="form-control"
                        />
                      ) : (
                        <p className="text-dark mb-0">{profile?.years_of_experience || 'Not specified'}</p>
                      )}
                    </div>

                    <div className="col-md-6">
                      <label className="form-label fw-medium">
                        <FaGraduationCap className="text-primary me-2" />
                        Institution
                      </label>
                      {isEditing ? (
                        <input
                          type="text"
                          value={formData.institution || ''}
                          onChange={(e) => handleInputChange('institution', e.target.value)}
                          className="form-control"
                        />
                      ) : (
                        <p className="text-dark mb-0">{profile?.institution || 'Not specified'}</p>
                      )}
                    </div>

                    <div className="col-md-6">
                      <label className="form-label fw-medium">Specialization</label>
                      {isEditing ? (
                        <input
                          type="text"
                          value={formData.specialization || ''}
                          onChange={(e) => handleInputChange('specialization', e.target.value)}
                          className="form-control"
                        />
                      ) : (
                        <p className="text-dark mb-0">{profile?.specialization || 'Not specified'}</p>
                      )}
                    </div>

                    {/* Membership Information */}
                    <div className="col-12 mt-3">
                      <h4 className="h5 fw-bold text-dark mb-3">
                        <FaIdCard className="text-primary me-2" />
                        Membership Information
                      </h4>
                    </div>

                    <div className="col-md-6">
                      <label className="form-label fw-medium">Membership Type</label>
                      <p className="text-dark mb-0 fw-bold text-capitalize">{profile?.membership_type_display || profile?.membership_type || 'Not specified'}</p>
                      <small className="text-muted">Contact admin to change type</small>
                    </div>

                    <div className="col-md-6">
                      <label className="form-label fw-medium">Membership Status</label>
                      <div>{getMembershipStatusBadge(profile?.membership_status_display || profile?.membership_status || 'pending')}</div>
                    </div>

                    {/* Bio */}
                    <div className="col-12 mt-3">
                      <label className="form-label fw-medium">Bio</label>
                      {isEditing ? (
                        <textarea
                          value={formData.bio || ''}
                          onChange={(e) => handleInputChange('bio', e.target.value)}
                          rows={4}
                          className="form-control"
                          placeholder="Tell us about yourself..."
                        />
                      ) : (
                        <p className="text-dark mb-0">{profile?.bio || 'No bio provided'}</p>
                      )}
                    </div>

                    {/* ── Vendor Company Details (only shown for vendor members) ── */}
                    {profile?.membership_type === 'vendor' && (
                      <>
                        <div className="col-12 mt-4">
                          <hr />
                          <h4 className="h5 fw-bold mb-3" style={{ color: '#556B2F' }}>
                            <FaBuilding className="me-2" style={{ color: '#6B8E23' }} />
                            Company / Business Details
                          </h4>
                        </div>

                        {/* Vendor Logo */}
                        {profile?.vendor_logo && (
                          <div className="col-12 mb-3">
                            <label className="form-label fw-medium">Company Logo</label>
                            <div>
                              <img
                                src={profile.vendor_logo.startsWith('http')
                                  ? profile.vendor_logo
                                  : `${process.env.REACT_APP_BACKEND_URL || 'https://api.africaresp.org'}${profile.vendor_logo}`}
                                alt="Company Logo"
                                style={{ maxHeight: '80px', maxWidth: '200px', objectFit: 'contain', border: '1px solid #dee2e6', borderRadius: '8px', padding: '6px' }}
                              />
                            </div>
                          </div>
                        )}

                        <div className="col-md-6">
                          <label className="form-label fw-medium">
                            <FaBuilding className="text-success me-2" />
                            Company Name
                          </label>
                          <p className="text-dark mb-0 fw-bold">{profile?.company_name || 'Not specified'}</p>
                        </div>

                        <div className="col-md-6">
                          <label className="form-label fw-medium">
                            <FaStar className="me-2" style={{ color: '#6B8E23' }} />
                            Vendor Tier
                          </label>
                          <p className="mb-0">
                            <span className="badge px-3 py-2 fw-bold" style={{
                              backgroundColor:
                                profile?.vendor_tier === 'Diamond' ? '#b9f2ff' :
                                  profile?.vendor_tier === 'Platinum' ? '#e5e4e2' :
                                    profile?.vendor_tier === 'Gold' ? '#FFD700' :
                                      profile?.vendor_tier === 'Silver' ? '#C0C0C0' : '#CD7F32',
                              color: '#333'
                            }}>
                              {profile?.vendor_tier || 'Bronze'} Partner
                            </span>
                          </p>
                        </div>

                        <div className="col-md-6">
                          <label className="form-label fw-medium">Company Rep / Contact Person</label>
                          <p className="text-dark mb-0">{profile?.contact_person_name || 'Not specified'}</p>
                        </div>

                        <div className="col-md-6">
                          <label className="form-label fw-medium">Rep Title / Role</label>
                          <p className="text-dark mb-0">{profile?.contact_person_title || 'Not specified'}</p>
                        </div>

                        <div className="col-md-6">
                          <label className="form-label fw-medium">
                            <FaPhone className="text-success me-2" />
                            Business Phone
                          </label>
                          <p className="text-dark mb-0">{profile?.vendor_phone || profile?.phone || 'Not specified'}</p>
                        </div>

                        <div className="col-md-6">
                          <label className="form-label fw-medium">
                            <FaGlobe className="text-success me-2" />
                            Website
                          </label>
                          {profile?.vendor_website ? (
                            <a href={profile.vendor_website} target="_blank" rel="noopener noreferrer" className="text-primary">
                              {profile.vendor_website}
                            </a>
                          ) : (
                            <p className="text-dark mb-0">Not specified</p>
                          )}
                        </div>

                        <div className="col-md-6">
                          <label className="form-label fw-medium">
                            <FaStore className="text-success me-2" />
                            Category / Industry
                          </label>
                          <p className="text-dark mb-0">{profile?.vendor_category || 'Not specified'}</p>
                        </div>

                        <div className="col-md-6">
                          <label className="form-label fw-medium">Company Size</label>
                          <p className="text-dark mb-0">{profile?.company_size || 'Not specified'}</p>
                        </div>

                        <div className="col-12">
                          <label className="form-label fw-medium">Company Address</label>
                          <p className="text-dark mb-0">
                            {[profile?.vendor_street_address || profile?.company_address, profile?.vendor_city, profile?.vendor_state_province, profile?.vendor_country]
                              .filter(Boolean).join(', ') || 'Not specified'}
                          </p>
                        </div>

                        {profile?.vendor_description && (
                          <div className="col-12">
                            <label className="form-label fw-medium">Company Description</label>
                            <p className="text-dark mb-0">{profile.vendor_description}</p>
                          </div>
                        )}

                        {profile?.partnership_goals && (
                          <div className="col-12">
                            <label className="form-label fw-medium">Partnership Goals</label>
                            <p className="text-dark mb-0">{profile.partnership_goals}</p>
                          </div>
                        )}

                        <div className="col-12">
                          <label className="form-label fw-medium">Application Status</label>
                          <p className="mb-0">
                            <span className={`badge bg-${profile?.vendor_status === 'approved' ? 'success' :
                              profile?.vendor_status === 'rejected' ? 'danger' :
                                profile?.vendor_status === 'suspended' ? 'secondary' : 'warning'
                              } px-3 py-2`}>
                              {profile?.vendor_status ? profile.vendor_status.charAt(0).toUpperCase() + profile.vendor_status.slice(1) : 'Pending Review'}
                            </span>
                          </p>
                        </div>
                      </>
                    )}
                  </div>

                  {isEditing && (
                    <div className="mt-4 pt-4 border-top d-flex justify-content-end gap-2">
                      <button
                        onClick={() => {
                          setIsEditing(false);
                          setFormData(profile); // Reset form data
                        }}
                        className="btn btn-secondary"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSaveProfile}
                        className="btn btn-primary"
                      >
                        Save Changes
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
};

export default ProfilePage;